<script setup lang="ts">
import { computed, ref, watch, onBeforeUnmount } from 'vue';

const props = withDefaults(
  defineProps<{
    modelValue: [number, number];
    min?: number;
    max?: number;
    step?: number;
    color?: string;
  }>(),
  {
    min: 0,
    max: 100,
    step: 1,
    color: 'oklch(68.1% .162 75.834)',
  },
);

const emit = defineEmits<{
  'update:modelValue': [value: [number, number]];
  change: [value: [number, number]];
  input: [value: [number, number]];
}>();

const trackRef = ref<HTMLElement | null>(null);
const dragging = ref<'start' | 'end' | null>(null);

// Local state drives rendering during drag — avoids parent round-trip per frame
const localValue = ref<[number, number]>([...props.modelValue] as [number, number]);

watch(
  () => props.modelValue,
  (v) => {
    if (!dragging.value) localValue.value = [...v] as [number, number];
  },
);

const range = computed(() => props.max - props.min);
const startPct = computed(() => ((localValue.value[0] - props.min) / range.value) * 100);
const endPct = computed(() => ((localValue.value[1] - props.min) / range.value) * 100);

function clamp(val: number, lo: number, hi: number) {
  return Math.min(hi, Math.max(lo, val));
}

function snap(val: number) {
  return Math.round(val / props.step) * props.step;
}

function pctToValue(pct: number): number {
  const raw = props.min + (pct / 100) * range.value;
  return clamp(snap(raw), props.min, props.max);
}

function getPointerPct(e: MouseEvent | TouchEvent): number {
  if (!trackRef.value) return 0;
  const rect = trackRef.value.getBoundingClientRect();
  const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
  return clamp(((clientX - rect.left) / rect.width) * 100, 0, 100);
}

let rafId = 0;

function onPointerDown(e: MouseEvent | TouchEvent, handle: 'start' | 'end') {
  e.preventDefault();
  dragging.value = handle;
  document.addEventListener('mousemove', onPointerMove, { passive: false });
  document.addEventListener('mouseup', onPointerUp);
  document.addEventListener('touchmove', onPointerMove, { passive: false });
  document.addEventListener('touchend', onPointerUp);
}

function onPointerMove(e: MouseEvent | TouchEvent) {
  if (!dragging.value) return;
  e.preventDefault();
  const pct = getPointerPct(e);
  const val = pctToValue(pct);
  const [s, en] = localValue.value;

  if (dragging.value === 'start') {
    localValue.value = [Math.min(val, en), en];
  } else {
    localValue.value = [s, Math.max(val, s)];
  }

  // Only emit lightweight input event for label display — no model update during drag
  if (!rafId) {
    rafId = requestAnimationFrame(() => {
      rafId = 0;
      emit('input', [...localValue.value] as [number, number]);
    });
  }
}

function onPointerUp() {
  dragging.value = null;
  document.removeEventListener('mousemove', onPointerMove);
  document.removeEventListener('mouseup', onPointerUp);
  document.removeEventListener('touchmove', onPointerMove);
  document.removeEventListener('touchend', onPointerUp);
  if (rafId) {
    cancelAnimationFrame(rafId);
    rafId = 0;
  }
  emit('update:modelValue', [...localValue.value] as [number, number]);
  emit('change', [...localValue.value] as [number, number]);
}

function onTrackClick(e: MouseEvent) {
  if (dragging.value) return;
  const pct = getPointerPct(e);
  const val = pctToValue(pct);
  const [s, en] = localValue.value;
  const distStart = Math.abs(val - s);
  const distEnd = Math.abs(val - en);
  let newValue: [number, number];
  if (distStart <= distEnd) {
    newValue = [Math.min(val, en), en];
  } else {
    newValue = [s, Math.max(val, s)];
  }
  localValue.value = newValue;
  emit('update:modelValue', newValue);
  emit('change', newValue);
}

onBeforeUnmount(() => {
  if (rafId) cancelAnimationFrame(rafId);
  document.removeEventListener('mousemove', onPointerMove);
  document.removeEventListener('mouseup', onPointerUp);
  document.removeEventListener('touchmove', onPointerMove);
  document.removeEventListener('touchend', onPointerUp);
});
</script>

<template>
  <div ref="trackRef" class="range-slider" @mousedown="onTrackClick">
    <div class="range-slider__rail" />
    <div
      class="range-slider__range"
      :style="{ left: startPct + '%', width: endPct - startPct + '%', backgroundColor: color }"
    />
    <div
      class="range-slider__handle"
      :style="{ left: startPct + '%', borderColor: color }"
      @mousedown.stop="onPointerDown($event, 'start')"
      @touchstart.stop="onPointerDown($event, 'start')"
    />
    <div
      class="range-slider__handle"
      :style="{ left: endPct + '%', borderColor: color }"
      @mousedown.stop="onPointerDown($event, 'end')"
      @touchstart.stop="onPointerDown($event, 'end')"
    />
  </div>
</template>

<style scoped>
.range-slider {
  position: relative;
  height: 20px;
  cursor: pointer;
  touch-action: none;
  user-select: none;
}

.range-slider__rail {
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 4px;
  transform: translateY(-50%);
  border-radius: 2px;
  background: var(--p-surface-200);
}

.range-slider__range {
  position: absolute;
  top: 50%;
  height: 4px;
  transform: translateY(-50%);
  border-radius: 2px;
}

.range-slider__handle {
  position: absolute;
  top: 50%;
  width: 18px;
  height: 18px;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  border: 3px solid;
  background: var(--p-content-background, #fff);
  cursor: grab;
  transition: box-shadow 0.12s;
}

.range-slider__handle:hover,
.range-slider__handle:active {
  box-shadow: 0 0 0 4px rgb(0 0 0 / 8%);
}

.range-slider__handle:active {
  cursor: grabbing;
}
</style>
